import React, { FC, useMemo } from "react";
import { Box, View } from "@lookiero/aurora";
import { useI18nMessage } from "@lookiero/i18n-react";
import { Text } from "@lookiero/sty-psp-ui";
import { Promo } from "../../../../../../domain/entities/order/Order";
import { useFormatCurrency } from "../../../../hooks/useFormatCurrency";
import { DOMAIN, I18nMessages } from "../../../../i18n/i18n";
import { financial } from "../../../../shared/util/financial/financial";
import { style as totalTableStyle } from "./TotalTable.style";
import { TotalTableLine } from "./totalTableLine/TotalTableLine";

interface TotalTableProps {
  readonly amountToDiscount?: number;
  readonly discardedItemsPrice?: number;
  readonly initialPrice: number;
  readonly itemsCount: number;
  readonly originalTotalPrice?: number;
  readonly promo: Promo | undefined;
  readonly returnedTotalPrice: number;
  readonly totalPrice: number;
  readonly unavailableItemsPrice: number;
}

const TotalTable: FC<TotalTableProps> = ({
  amountToDiscount,
  discardedItemsPrice,
  initialPrice,
  itemsCount,
  originalTotalPrice,
  promo,
  returnedTotalPrice,
  totalPrice,
  unavailableItemsPrice,
}) => {
  const formatCurrency = useFormatCurrency();

  const orderTotalItemsText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.ORDER_TOTAL_ITEMS,
    values: { totalItems: itemsCount },
  });
  const orderTotalItemText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.ORDER_TOTAL_ITEM,
    values: { totalItems: itemsCount },
  });
  const orderTotalItemsUnavailableText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.ORDER_TOTAL_ITEMS_UNAVAILABLE,
  });
  const orderTotalItemsReturnedText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.ORDER_TOTAL_ITEMS_RETURNED,
  });
  const totalLineDiscardedText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.TOTAL_LINE_DISCARDED,
  });
  const orderFreeText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.ORDER_FREE,
  });
  const orderShippingText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.ORDER_SHIPPING,
  });
  const orderTotalText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.ORDER_TOTAL,
  });

  const style = useMemo(() => totalTableStyle(), []);

  return (
    <Box>
      {itemsCount && initialPrice !== undefined && (
        <TotalTableLine
          amount={formatCurrency({ amount: financial(originalTotalPrice || initialPrice) })}
          title={itemsCount > 1 ? orderTotalItemsText : orderTotalItemText}
        />
      )}
      {Boolean(unavailableItemsPrice && unavailableItemsPrice > 0) && (
        <TotalTableLine
          amount={formatCurrency({ amount: financial(-unavailableItemsPrice) })}
          title={orderTotalItemsUnavailableText}
        />
      )}
      {Boolean(returnedTotalPrice && returnedTotalPrice > 0) && (
        <TotalTableLine
          amount={formatCurrency({ amount: financial(-returnedTotalPrice) })}
          title={orderTotalItemsReturnedText}
        />
      )}
      {discardedItemsPrice !== undefined && Boolean(discardedItemsPrice && discardedItemsPrice > 0) && (
        <TotalTableLine
          amount={formatCurrency({ amount: financial(-discardedItemsPrice) })}
          title={totalLineDiscardedText}
        />
      )}
      {promo && (
        <TotalTableLine
          amount={amountToDiscount ? `-${formatCurrency({ amount: financial(amountToDiscount) })}` : ""}
          title={`${promo.code} (${`-${promo.discountPercentage}%`})`}
        />
      )}
      <TotalTableLine amount={orderFreeText} title={orderShippingText} />
      <View style={style.priceTotalLine}>
        <Text level={2} variant="action">
          {orderTotalText}
        </Text>
        <Text level={2} variant="action">
          {formatCurrency({ amount: financial(totalPrice) })}
        </Text>
      </View>
    </Box>
  );
};

export { TotalTable };
